/**
 * @author Iskra Popova
 *
 * SceneView class.
 * Builds a view from assetworks exported scene data.
 */

import BaseView, { ViewOrientation } from "./BaseView";
import SceneViewContainer from "./SceneViewContainer";
import {
    IViewConfigSchema,
    IObjectConfig,
    ITextConfig,
    ILayersList,
} from "../baseConfigs/layoutConfigShema";
import { Map } from "es6-shim";
import App from "../App";
// import { BitmapMoneyField } from "./BitmapMoneyField";
import { AbstractBitmapMoneyField, BitmapMoneyFieldConfig } from "./AbstractBitmapMoneyField";
import Button from "./components/Button";
import * as Particles from "pixi-particles";
import TextInput from "./components/TextInput";
import Locale from "../utils/Locale";
import ParticleLoop from "../utils/ParticleLoop";
import Layout, { PIXIElement } from "../utils/Layout";
import { AbstractMoneyField, MoneyFieldConfig } from "./AbstractMoneyField";
// import { MoneyField } from "./MoneyField";
import GameModel from "../../src/model/GameModel";
import SwitchGroup from "./components/SwitchGroup";
import SwitchButton from "./components/SwitchButton";
import TextField from "./components/TextField";
import Link from "./components/Link";
import BitmapTextField from "./components/BitmapTextField";
import Utils from "../utils/Utils";
import { Point } from "pixi.js";
import InstanceFactory from "../factories/InstanceFactory";
import DragonBonesAnimation from "./DragonBonesAnimation";
import SpineAnimation from './SpineAnimation';
import ConfigManager from '../managers/ConfigManager';
import { ConfigurationTypes } from '../const/ConfigurationTypes';
import ISceneSpineAnimConfig from '../managers/contracts/configurations/ISceneSpineAnimConfig';
import ISpineAnimConfig from '../managers/contracts/configurations/spine_anim/ISpineAnimConfig';
import ISceneOptions from '../contracts/ISceneOptions';

export default class SceneView extends BaseView {
    // protected positions
    public sceneSprites: Map<string, PIXI.Sprite>;
    public shapes: Map<string, PIXI.Graphics>;
    public links: Map<string, Link>;
    public sceneTextFields: Map<
        string,
        PIXI.Text | BitmapTextField | TextField
    >;
    public inputTextFields: Map<string, TextInput>;
    public sceneMoneyFields: Map<string, AbstractMoneyField | AbstractBitmapMoneyField>;
    public positions: Map<string, IObjectConfig>;
    public masks: Map<string, IObjectConfig>;
    public bounds: Map<string, IObjectConfig>;
    public buttons: Map<string, Button>;
    public buttonGroups: Map<string, SwitchGroup>;
    public anims: Map<string, PIXI.extras.AnimatedSprite>;
    public dragonBones: Map<string, DragonBonesAnimation>;
    public spineAnimations: Map<string, SpineAnimation>;
    public particles: Map<string, Particles.Emitter>;

    public sceneBounds: IObjectConfig;
    public maskGraphics: PIXI.Graphics;
    public groups: Map<string, PIXI.Container>;
    public containers: Map<string, SceneViewContainer>;
    public subViews: Map<
        string,
        BaseView | PIXI.DisplayObject | Array<BaseView | PIXI.DisplayObject>
    >;

    protected hasMask = false;
    protected sceneConfig: IViewConfigSchema;
    protected sceneConfigPortrait: IViewConfigSchema;
    protected _orientationChangeCallback: (...args: any[]) => void;
    protected _preOrientationChangeCallback: (...args: any[]) => void;
    protected _rescaleChangeCallback: (...args: any[]) => void;

    constructor(
        sceneConfig: IViewConfigSchema,
        sceneConfigPortrait?: IViewConfigSchema,
        options?: ISceneOptions
    ) {
        // super(sceneConfigPortrait != null && sceneConfigPortrait != undefined);
        super(GameModel.isMobile());
        // this.sceneBounds = this.parseBoundConfig(sceneConfig.sceneBound);
        this.sceneConfig = sceneConfig;
        this.sceneConfigPortrait = sceneConfigPortrait;
        let initConfig: IViewConfigSchema = Utils.deepClone(sceneConfig);
        if (
            this.orientation === ViewOrientation.Portrait &&
            this.sceneConfigPortrait
        ) {
            initConfig.bounds = this.sceneConfigPortrait.bounds
                ? Utils.deepClone(this.sceneConfigPortrait.bounds)
                : initConfig.bounds;
            initConfig.text = this.sceneConfigPortrait.text
                ? Utils.deepClone(this.sceneConfigPortrait.text)
                : initConfig.text;
            if (initConfig.groups && this.sceneConfigPortrait.groups) {
                // initConfig.groups = this.sceneConfigPortrait.groups ?Utils.deepClone(this.sceneConfigPortrait.text ):initConfig.groups;
                // initConfig.groups.forEach((group) => {
                for (const groupId in initConfig.groups) {
                    const group = initConfig.groups[groupId];
                    const portraitGroup = this.sceneConfigPortrait.groups[
                        groupId
                    ];
                    if (portraitGroup) {
                        initConfig.groups[groupId].bounds = portraitGroup.bounds
                            ? Utils.deepClone(portraitGroup.bounds)
                            : group.bounds;
                        initConfig.groups[groupId].text = portraitGroup.text
                            ? Utils.deepClone(portraitGroup.text)
                            : group.text;
                    }
                }

                // });
            }
        }
        this.parseConfig(initConfig, options);
        if (this.orientation === ViewOrientation.Portrait) {
            this.parseConfig(sceneConfigPortrait || sceneConfig);
        }
        // App.instance.start();
    }

    public set preOrientationChangeCallback(callback: (...args: any[]) => void) {
        if (GameModel.isMobile() && this.supportOrientationChange) {
            this._preOrientationChangeCallback = callback;
        }
    }

    public get preOrientationChangeCallback(): (...args: any[]) => void {
        return this._preOrientationChangeCallback;
    }

    public set orientationChangeCallback(callback: (...args: any[]) => void) {
        if (GameModel.isMobile() && this.supportOrientationChange) {
            this._orientationChangeCallback = callback;
        }
    }

    public get orientationChangeCallback(): (...args: any[]) => void {
        return this._orientationChangeCallback;
    }

    public set rescaleChangeCallback(callback: (...args: any[]) => void) {
        this._rescaleChangeCallback = callback;
    }

    public get rescaleChangeCallback(): (...args: any[]) => void {
        return this._rescaleChangeCallback;
    }

    public getPositionDefinitions(): Map<string, IObjectConfig> {
        if (!this.positions) {
            return;
        }

        const posMap = new Map<string, IObjectConfig>();
        this.positions.forEach((val, key) => {
            posMap.set(key, this.parseBoundConfig(val));
        });

        return posMap;
    }

    public forceLandscape() {
        if (this.orientation === ViewOrientation.Landscape) {
            return;
        }
        this.orientation = ViewOrientation.Landscape;
        this.parseConfig(this.sceneConfig);
    }

    public forcePortrait() {
        if (this.orientation === ViewOrientation.Portrait) {
            return;
        }
        this.orientation = ViewOrientation.Portrait;
        this.parseConfig(this.sceneConfigPortrait || this.sceneConfig);
    }

    protected parseConfig(sceneConfig?: IViewConfigSchema, options?: ISceneOptions) {
        if (!sceneConfig) {
            sceneConfig = this.currentSceneConfig;
        }
        if (sceneConfig.sceneBound) {
            this.sceneBounds = this.parseBoundConfig(sceneConfig.sceneBound);
        }
        if (this.buttonGroups) {
            this.buttonGroups.forEach((group) => {
                group.getChildren().forEach((btn) => {
                    btn.removeFromParent();
                });
            });
        }

        const dragonBonesArmature = this.getDragonBonesArmature(sceneConfig.armatures);

        this.parseShapes(sceneConfig.shapes);
        this.parsePositions(sceneConfig.positions);
        this.parseMasks(sceneConfig.masks);
        this.parseBounds(sceneConfig.bounds);
        this.parseSprites(sceneConfig.sprites);
        this.parseDragonBonesAnims(dragonBonesArmature);
        this.parseAnims(sceneConfig.anims);
        let mask = this.addMask(sceneConfig.sceneMask);
        this.hasMask = mask != null;
        this.parseParticles(sceneConfig.particles, this);
        // if(!this.containers){
        this.parseContainers(sceneConfig.containers);
        // }
        this.parseButtons(sceneConfig.buttons);
        // this.parseContainers(sceneConfig.containers);
        this.parseTexts(sceneConfig.text, options);
        this.parseGroups(sceneConfig.groups, options);
        if (this.sceneSprites) {

            this.sceneSprites.forEach((sprite, key) => {
                if (key.indexOf("displace-") != -1) {
                    // obj.cacheAsBitmap = true;
                    // sprite.texture = App.assets.getTexture(key);
                    let filter = new PIXI.filters.DisplacementFilter(sprite);
                    filter.autoFit = true;
                    sprite.texture.baseTexture.wrapMode = PIXI.WRAP_MODES.REPEAT;
                    sprite.parent.filters = [filter];
                }
            })
        }

        if (this.particles) {
            this.particles.forEach(particle => {
                if (this.hasMask && (particle.spawnType === "point" || particle.spawnType === "burst")) {
                    particle.parent.mask = this.maskGraphics;
                }
            })
        }

        if (!this.hasMask) {
            this.mask = null;
        }
    }

    public getSubView(
        name: string
    ): BaseView | PIXI.DisplayObject | Array<BaseView | PIXI.DisplayObject> {
        if (!this.subViews) {
            return null;
        }
        return this.subViews.get(name);
    }

    public addSubView(
        subView: BaseView | PIXI.DisplayObject,
        name: string,
        containerName?: string,
        deleteteOld: boolean = false
    ) {
        if (!this.subViews) {
            this.subViews = new Map<
                string,
                | BaseView
                | PIXI.DisplayObject
                | Array<BaseView | PIXI.DisplayObject>
            >();
        }

        if (this.containers && this.containers.has(containerName)) {
            let cont = this.containers.get(containerName);
            // subView.x = -cont.x;
            // subView.y = -cont.y;
            cont.addChild(subView);
        } else {
            this.addChild(subView);
        }

        if (this.subViews.has(name)) {
            const old = this.subViews.get(name);
            if (deleteteOld) {
                this.removeSubView(name);
                this.subViews.set(name, subView);
            } else {
                if (Array.isArray(old)) {
                    old.push(subView);
                } else {
                    this.subViews.set(name, [old, subView]);
                }
            }
        } else {
            this.subViews.set(name, subView);
        }
    }

    public removeSubView(
        name: string
    ) {
        let views = this.getSubView(name);
        if (Array.isArray(views)) {
            views.forEach((view) => {
                if (view.parent) {
                    view.parent.removeChild(view);
                }
            });
        } else {
            if (views.parent) {
                views.parent.removeChild(views);
            }
        }
        this.subViews.delete(name);
    }

    protected parseGroups(groups: ILayersList, options?: ISceneOptions) {
        if (!groups) {
            return;
        }

        if (!this.groups) {
            this.groups = new Map<string, PIXI.Container>();
        }

        let startIndex = this.children ? this.children.length : 0;
        for (const groupId in groups) {
            const group = groups[groupId];
            const id = groupId;
            if (group.sceneBound) {
                this.sceneBounds = this.parseBoundConfig(group.sceneBound);
            }

            const groupShapes = this.parseShapes(group.shapes);
            this.parsePositions(group.positions);
            this.parseMasks(group.masks);
            this.parseBounds(group.bounds);
            let groupContainer: PIXI.Container;
            if (this.groups.has(id)) {
                groupContainer = this.groups.get(id);
                groupContainer.removeChildren();
            } else {
                groupContainer = new PIXI.Container();
                groupContainer.name = id;
                this.groups.set(id, groupContainer);
                this.addChildAt(groupContainer, startIndex);
            }
            const spineArmature = this.getSpineArmature(group.armatures);
            const dragonBonesArmature = this.getDragonBonesArmature(group.armatures);

            const groupContainers = this.parseContainers(group.containers);
            const groupSprites = this.parseSprites(group.sprites);

            const groupArmatures = [...this.parseDragonBonesAnims(dragonBonesArmature), ...this.parseSpineAnims(spineArmature)];
            const groupAnims = this.parseAnims(group.anims);
            let mask = this.addMask(group.sceneMask);
            const groupParticles = this.parseParticles(
                group.particles,
                groupContainer
            );
            const groupBtns = this.parseButtons(group.buttons);

            const groupTexts = this.parseTexts(group.text, options);
            const groupList: PIXIElement[] = [];
            if (mask) {
                groupList.push(mask);
                this.hasMask = true;
            }

            if (groupShapes && groupShapes.length) {
                groupList.push(...groupShapes);
            }
            if (groupSprites && groupSprites.length) {
                groupList.push(...groupSprites);
            }

            if (groupBtns && groupBtns.length) {
                groupList.push(...groupBtns);
            }

            if (groupArmatures && groupArmatures.length) {
                groupList.push(...groupArmatures);
            }

            if (groupAnims && groupAnims.length) {
                groupList.push(...groupAnims);
            }

            if (groupParticles && groupParticles.length) {
                groupList.push(...groupParticles);
            }

            if (groupContainers && groupContainers.length) {
                groupList.push(...groupContainers);
            }

            if (groupTexts && groupTexts.length) {
                groupList.push(...groupTexts);
            }
            groupList.forEach((el, index) => {
                groupContainer.addChildAt(el, index);
            });
            if (groupBtns) {
                groupBtns.forEach((btn) => {
                    if (btn instanceof SwitchGroup) {
                        btn.onRotate();
                    }
                });
            }
            groupContainer.x = Layout.scalePixels(group.x);
            groupContainer.y = Layout.scalePixels(group.y);

            // console.log(group);
        }
    }

    protected getSpineArmature = (armatures: IObjectConfig[]): IObjectConfig[] => {
        const result = [];
        armatures.forEach(armature => {
            if (armature.id.includes("spine")) {
                result.push({ ...armature })
            }
        })

        return result
    }
    protected getDragonBonesArmature = (armatures: IObjectConfig[]): IObjectConfig[] => {
        const result = [];
        armatures.forEach(armature => {
            if (!armature.id.includes("spine")) {
                result.push({ ...armature })
            }
        })

        return result
    }

    protected parseSpineAnims(armatures: IObjectConfig[]): PIXI.Container[] {
        if (!armatures) {
            return;
        }
        const list = [];

        armatures.forEach(armature => {
            const [idData, count] = armature.id.split('~');
            let id = idData.split("^")[1];
            const animConfig = ConfigManager.getConfig(ConfigurationTypes.SPINE_ANIMATIONS).getAnimConfig(id);

            let containerData = this.parseSpineAnimContainer(armature, animConfig);

            let container = containerData.container;

            list.push(container);
            if (count) {
                container.name = `${armature.id}_${count}`;
            } else {
                container.name = armature.id;
            }

            let spineAnim: SpineAnimation;

            let animId = count ? `${id}_${count}` : id;

            // if (this.spineAnimations && this.spineAnimations.has(animId) && animConfig.createNewContainer) {
            // let obj = this.spineAnimations.get(animId);
            // if (obj.parent) {
            //     obj.parent.removeChild(obj)
            // }
            // container.addChild(obj);
            ///////////////////////////////////////////////////////////////////////////
            // obj.destroy()
            // this.spineAnimations.delete(animId)
            // obj.scale.set(1);
            // }

            if (this.spineAnimations && this.spineAnimations.has(animId)) {
                /*
                When orientation changed search for specific portrait animation states
                and replace it white same portrait state
                */
                // if (id === "popup_fs") {
                //     console.log(container);
                //     console.log(armature);
                // }

                spineAnim = this.spineAnimations.get(animId);

                let change = true;

                if (change) {

                    if (Layout.isLandscape()) {
                        // debugger
                        const expressions: Function[] = [];
                        const animsWithoutLandscapeStates: string[] = [];
                        spineAnim.state.tracks.forEach((obj, index) => {
                            if (obj?.animation?.name.includes("_pt")) {
                                // let exp = () => spineAnim.play(obj.animation.name.split("_pt")[0], obj.loop, obj.timeScale);
                                let exp = () => {
                                    spineAnim.state.setAnimation(index, obj.animation.name.split("_pt")[0], obj.loop,)
                                    spineAnim.state.timeScale = obj.timeScale;
                                }
                                    ;
                                expressions.push(exp)
                            } else {
                                if (obj?.animation?.name) {
                                    animsWithoutLandscapeStates.push(obj.animation.name)
                                }
                            }
                        })

                        if (expressions.length > 0) {
                            spineAnim.stop()
                            spineAnim.position.set(
                                Layout.scalePixels(armature.width * (containerData.containerSizeIndex / 2)),
                                Layout.scalePixels(armature.height * (containerData.containerSizeIndex / 2))
                            );

                            spineAnim.scale.set(this.layout.globalScale);
                        }

                        if (animsWithoutLandscapeStates.length > 0) {
                            console.warn(
                                `[SPINE ANIMATION] -> animation: <${animId}> states <${animsWithoutLandscapeStates}> don't have landscape states or state is not declared in configuration file! `,
                            );
                        }
                        expressions.forEach(expression => expression())
                    } else {
                        const expressions: Function[] = [];
                        const animsWithoutPtStates: string[] = [];
                        spineAnim.state.tracks.forEach((obj, index) => {

                            if (obj && animConfig.states.find(data => data.name === `${obj.animation.name}_pt`)) {
                                // let exp = () => spineAnim.play(`${obj.animation.name}_pt`, obj.loop, obj.timeScale);
                                // expressions.push(exp)
                                let exp = () => {
                                    spineAnim.state.setAnimation(index, `${obj.animation.name}_pt`, obj.loop)
                                    spineAnim.state.timeScale = obj.timeScale;
                                }
                                    ;
                                expressions.push(exp)
                            } else {
                                if (obj?.animation?.name) {
                                    animsWithoutPtStates.push(obj.animation.name)
                                }
                            }
                        })

                        if (expressions.length > 0 || animConfig.forceRepositionContainer) {
                            spineAnim.stop()
                            spineAnim.position.set(
                                Layout.scalePixels(armature.width * (containerData.containerSizeIndex / 2)),
                                Layout.scalePixels(armature.height * (containerData.containerSizeIndex / 2))
                            );
                            spineAnim.scale.set(this.layout.globalScale);
                            // container.changeBound(armature);
                            // debugger
                            // THIS IS A MAGIC !!!! HARDCODED NUMBERS ARE INSANE !!!!
                            // spineAnim.position.set(
                            //     Layout.scalePixels((armature.x - armature.width / 2)),
                            //     Layout.scalePixels(armature.height * (containerData.containerSizeIndex / 2))
                            // );
                            // spineAnim.position.set(
                            //         Layout.scalePixels(armature.width / 1.77 * (containerData.containerSizeIndex / 1.3)),
                            //         Layout.scalePixels(armature.height / 1.77 * (containerData.containerSizeIndex / 1.3))
                            //     );
                            // spineAnim.x -= 100
                            // spineAnim.scale.set(this.layout.globalScale * 1.55);
                            // spineAnim.scale.set(this.layout.globalScale);
                        }

                        if (animsWithoutPtStates.length > 0) {
                            console.warn(
                                `[SPINE ANIMATION] -> animation: <${animId}> states <${animsWithoutPtStates}> don't have portrait states or state is not declared in configuration file! `,
                            );
                        }
                        expressions.forEach(expression => expression());
                    }
                }

            } else {
                spineAnim = this.createSpineAnim(containerData, id, armature);
                this.playSpineAfterCreated(animConfig, spineAnim);
            }

            if (!this.spineAnimations) {
                this.spineAnimations = new Map<string, SpineAnimation>();
            }

            this.spineAnimations.set(animId, spineAnim);

        })

        return list;
    }

    protected createSpineAnim = (containerData: {
        containerSizeIndex: number,
        container: SceneViewContainer
    }, id: string, armature: IObjectConfig): SpineAnimation => {
        let spineAnim = this.assets.getSpineAnimation(id);
        containerData.container.addChild(spineAnim);

        spineAnim.scale.set(this.layout.globalScale);

        spineAnim.position.set(
            Layout.scalePixels(armature.width * (containerData.containerSizeIndex / 2)),
            Layout.scalePixels(armature.height * (containerData.containerSizeIndex / 2))
        );

        return spineAnim
    }

    protected playSpineAfterCreated = (animConfig: ISpineAnimConfig, spineAnim: SpineAnimation): void => {
        if (animConfig.playDefault) {
            if (animConfig.states.find(data => data.name === animConfig.defaultState)) {
                if (this.layout.isLandscape()) {
                    spineAnim.play();
                } else {
                    if (animConfig.states.find(data => data.name === `${animConfig.defaultState}_pt`)) {
                        spineAnim.play(`${animConfig.defaultState}_pt`)
                    } else {
                        spineAnim.play();
                    }
                }
            } else if (
                animConfig.chainingStates
                && animConfig.chainingStates.length > 0
                && animConfig.chainingStates.find(data => data.name === animConfig.defaultState)
            ) {
                if (this.layout.isLandscape()) {
                    spineAnim.playChainingStates(animConfig.defaultState)
                } else {
                    if (animConfig.chainingStates.find(data => data.name === `${animConfig.defaultState}_pt`)) {
                        spineAnim.playChainingStates(`${animConfig.defaultState}_pt`)
                    } else {
                        spineAnim.playChainingStates(animConfig.defaultState)
                    }
                }
            }
        }
    }

    protected parseSpineAnimContainer = (armature: IObjectConfig, animConfig: ISpineAnimConfig): {
        containerSizeIndex: number,
        container: SceneViewContainer
    } => {
        let result: {
            containerSizeIndex: number,
            container: SceneViewContainer
        };

        let containerSizeIndex = 1;

        // made container bigger then animation, prevent cutting the animation if is it bigger in one of him states
        if (animConfig.containerSize) {
            containerSizeIndex = animConfig.containerSize
        }

        let contData: IObjectConfig = {
            ...armature,
            width: armature.width * containerSizeIndex,
            height: armature.height * containerSizeIndex,
            id: armature.id,
            x: (armature.x - armature.width * ((containerSizeIndex - 1) / 2)),
            y: (armature.y - armature.height * ((containerSizeIndex - 1) / 2)),
        }

        if (this.containers.has(armature.id) && animConfig.forceRepositionContainer) {
            const bounds = this.parseBoundConfig(contData);
            const obj = this.containers.get(armature.id);
            obj.pivot.x = obj.pivot.y = 0;
            obj.changeSpineAnimBound(bounds, false);
            obj.x = Layout.scalePixels(bounds.x);
            obj.y = Layout.scalePixels(bounds.y);
            this.addChild(obj);

            result = {
                container: obj,
                containerSizeIndex: containerSizeIndex
            };
            // obj.scale.set(1);

            // obj.width = armature.width * containerSizeIndex;
            // obj.height = armature.height * containerSizeIndex;
            // obj.x = (armature.x - armature.width * ((containerSizeIndex - 1) / 2));
            // obj.y = (armature.y - armature.height * ((containerSizeIndex - 1) / 2));

        } else {
            result = {
                container: this.buildSceneViewContainer(contData),
                containerSizeIndex: containerSizeIndex
            };
        }



        // let size =  new PIXI.Graphics();
        // size.beginFill(0xf5f100, 5);
        // size.drawRect(0, 0, result.container.width, result.container.height);
        // result.container.addChild(size);
        // if (armature.id === "spine^popup_fs") {
        //     console.log(result.container.width, result.container.height);
        //     console.log(this.layout.globalScale);
        // }


        return result
    }

    protected parseDragonBonesAnims(armatures: IObjectConfig[]): PIXI.Container[] {
        if (!armatures) {
            return;
        }
        // let containers = this.parseContainers(armatures);
        const list = [];

        armatures.forEach(armature => {
            let container = this.buildSceneViewContainer(armature);
            list.push(container);
            let id = armature.id.split('~')[0];
            container.name = armature.id;
            let dbAnim: DragonBonesAnimation;
            if (this.dragonBones && this.dragonBones.has(armature.id)) {
                dbAnim = this.dragonBones.get(armature.id)
            } else {
                dbAnim = this.assets.getDragonBonesAnimation(id);
                container.addChild(dbAnim.armatureDisplay);
                dbAnim.armatureDisplay.x = Layout.scalePixels(armature.width) * 0.5;
                dbAnim.armatureDisplay.y = Layout.scalePixels(armature.height) * 0.5;
                dbAnim.armatureDisplay.scale.x = Layout.globalScale;
                dbAnim.armatureDisplay.scale.y = Layout.globalScale;
                dbAnim.play("EMPTY", 1);

            }
            if (!this.dragonBones) {
                this.dragonBones = new Map<string, DragonBonesAnimation>();
            }


            this.dragonBones.set(armature.id, dbAnim);

        })

        return list;
    }

    protected parseContainers(containers: IObjectConfig[]): PIXI.Container[] {
        if (!containers) {
            return;
        }

        if (!this.containers) {
            this.containers = new Map<string, SceneViewContainer>();
        }

        const list = [];

        containers.forEach((container) => {
            let obj = this.buildSceneViewContainer(container)
            list.unshift(obj);
        });

        return list;
    }

    protected buildSceneViewContainer(container: IObjectConfig): SceneViewContainer {
        const bounds = this.parseBoundConfig(container);

        const id = container.id;

        // if (id === "spine^popup_fs") {
        //     debugger
        // }

        let obj: SceneViewContainer;

        if (!this.containers.has(id)) {
            obj = new SceneViewContainer(bounds);
            this.containers.set(id, obj);
        } else {
            obj = this.containers.get(id);
            obj.pivot.x = obj.pivot.y = 0;
            obj.changeBound(bounds);
        }

        obj.x = Layout.scalePixels(bounds.x);
        obj.y = Layout.scalePixels(bounds.y);
        this.addChild(obj);

        return obj;
    }

    protected addMask(mask: IObjectConfig) {
        if (!mask) {
            return;
        }
        const bounds = this.parseBoundConfig(mask);
        if (!this.maskGraphics) {
            this.maskGraphics = new PIXI.Graphics();
            this.maskGraphics.beginFill(0x000000);
            this.maskGraphics.drawRect(
                0,
                0,
                this.layout.scalePixels(bounds.width),
                this.layout.scalePixels(bounds.height)
            );
            this.maskGraphics.endFill();
        }
        this.maskGraphics.x = Layout.scalePixels(bounds.x);
        this.maskGraphics.y = Layout.scalePixels(bounds.y);
        this.maskGraphics.width = this.layout.scalePixels(bounds.width);
        this.maskGraphics.height = this.layout.scalePixels(bounds.height);
        this.addChild(this.maskGraphics);
        this.mask = this.maskGraphics;
        return this.maskGraphics;
    }

    protected parseShapes(shapes: IObjectConfig[]): PIXI.Graphics[] {
        if (!shapes || !shapes.length) {
            return;
        }

        if (!this.shapes) {
            this.shapes = new Map<string, PIXI.Graphics>();
        }
        let list = [];
        // shape-cover-000000-75
        shapes.forEach((shape) => {
            const bounds = this.parseBoundConfig(shape);
            const props = shape.id.split("-");
            const id = props[1];
            const color = props[2];
            const alpha = props[3];
            let fillColor = 0x00000;
            if (color) {
                fillColor = parseInt("0x" + color, 16);
            }

            let obj: PIXI.Graphics;

            if (!this.shapes.has(id)) {
                obj = new PIXI.Graphics();
                obj.beginFill(fillColor, 1);
                obj.drawRect(
                    0,
                    0,
                    Layout.scalePixels(bounds.width),
                    Layout.scalePixels(bounds.height)
                );
                obj.endFill();
                this.shapes.set(id, obj);
            } else {
                obj = this.shapes.get(id);
            }

            obj.x = Layout.scalePixels(bounds.x);
            obj.y = Layout.scalePixels(bounds.y);
            // Layout.testSetWidth(obj, bounds.width, false);
            // Layout.testSetHeight(obj, bounds.height, false);
            obj.width = Layout.scalePixels(bounds.width);
            obj.height = Layout.scalePixels(bounds.height);
            if (alpha) {
                obj.alpha = parseFloat(alpha) / 100;
            }
            this.addChild(obj);
            list.unshift(obj);
        });

        return list;
    }

    protected parseButtons(
        buttons: IObjectConfig[],
        bounds?: IObjectConfig[]
    ): BaseView[] {
        if (!buttons || !buttons.length) {
            return;
        }
        if (!this.buttons) {
            this.buttons = new Map<string, Button>();
        }
        let list = [];
        const startIndex = this.children.length ? this.children.length : 0;
        for (const button in buttons) {
            const definition = buttons[button];
            let isSelectGroup = false;
            let isSelectBtn = false;
            let id = definition.id;
            let textureName = id;
            if (definition.id.indexOf("group-") !== -1) {
                isSelectGroup = true;
                id = definition.id.split("-")[1];
                textureName = id.split("~")[0];
            }
            if (definition.id.indexOf("select-") !== -1) {
                isSelectBtn = true;
                id = definition.id.split("-")[1];
                textureName = id.split("~")[0];
            }

            const prefix =
                definition.parentLayer.split("~%")[0] + "-" + textureName + "/";
            let btn: Button | SwitchButton;
            let btnIsNew = true;
            if (this.buttons.has(id)) {
                btn = this.buttons.get(id);
                btnIsNew = false;
            } else {
                if (isSelectGroup || isSelectBtn) {
                    btn = new SwitchButton(
                        {
                            up: prefix + "up",
                            over: prefix + "over",
                            down: prefix + "down",
                            off: prefix + "off",
                        },
                        {
                            up: prefix + "select_up",
                            over: prefix + "select_over",
                            down: prefix + "select_down",
                            off: prefix + "select_off",
                        }
                    );
                } else {
                    btn = new Button({
                        up: prefix + "up",
                        over: prefix + "over",
                        down: prefix + "down",
                        off: prefix + "off",
                    });
                }

                this.buttons.set(id, btn);
            }

            btn.setAnchor(0.5);
            this.addChildAt(btn, startIndex);
            const bound = this.parseBoundConfig(definition);

            if (isSelectGroup) {
                if (!this.buttonGroups) {
                    this.buttonGroups = new Map<string, SwitchGroup>();
                }
                if (!this.buttonGroups.has(textureName)) {
                    // if(this.containers.has(textureName))
                    this.buttonGroups.set(
                        textureName,
                        new SwitchGroup(this.containers.get(textureName))
                    );
                    // this.addChild(this.buttonGroups.get(textureName));
                }
                // this.containers.get(textureName).scale.x = this.containers.get(
                //     textureName
                // ).scale.y = 1;
                this.buttonGroups
                    .get(textureName)
                    .addToGroup(btn as SwitchButton);
                // this.buttonGroups.get(textureName).scale.x = this.buttonGroups.get(textureName).scale.y = btn.scale.x;

                btn.scale.x = btn.scale.y = 1 / this.containers.get(textureName).scale.x;

                let swichGorupBound = this.buttonGroups
                    .get(textureName)
                    .getContainer()
                    .getBoundConfig();
                // Layout.setX(this.buttonGroups.get(textureName) , bound.x + bound.width * 0.5 );
                // Layout.setY(this.buttonGroups.get(textureName), bound.y + bound.height * 0.5);

                if (list.indexOf(this.buttonGroups.get(textureName)) === -1) {
                    list.push(this.buttonGroups.get(textureName));
                }

                // if (btnIsNew) {
                Layout.testSetWidth(btn.butImg, bound.width);
                Layout.setX(
                    btn.butImg,
                    bound.x + bound.width * 0.5 - swichGorupBound.x
                );
                Layout.setY(
                    btn.butImg,
                    bound.y - swichGorupBound.y + bound.height * 0.5
                ); //swichGorupBound.y);
                // }
            } else {
                Layout.testSetWidth(btn.butImg, bound.width);
                Layout.setX(btn.butImg, bound.x + bound.width * 0.5);
                Layout.setY(btn.butImg, bound.y + bound.height * 0.5);
            }

            btn.setHitArea(textureName);

            // btn.setAnchor(0.5);
            // this.addChildAt(btn, startIndex);
            // const bound = this.parseBoundConfig(definition);
            // this.layout.testSetWidth(btn.butImg, bound.width);
            // this.buttons.set(id, btn);
            list.unshift(btn);
        }

        return list;
    }

    protected parsePositions(positions: IObjectConfig[]) {
        if (!positions) {
            return;
        }

        if (!this.positions) {
            this.positions = new Map<string, IObjectConfig>();
        }

        for (const position in positions) {
            this.positions.set(
                positions[position].id,
                this.parseBoundConfig(positions[position])
            );
        }
    }

    protected parseMasks(masks: IObjectConfig[]) {
        if (!masks) {
            return;
        }

        if (!this.masks) {
            this.masks = new Map<string, IObjectConfig>();
        }

        for (const position in masks) {
            this.masks.set(
                masks[position].id,
                this.parseBoundConfig(masks[position])
            );
        }
    }

    protected parseBounds(bounds: IObjectConfig[]) {
        if (!bounds) {
            return;
        }
        if (!this.bounds) {
            this.bounds = new Map<string, IObjectConfig>();
        }

        for (const position in bounds) {
            let id = bounds[position].id;
            const counter = parseInt(bounds[position].id.split("~")[1]);
            if (counter) {
                id = id + "~" + counter;
            }
            this.bounds.set(
                bounds[position].id,
                this.parseBoundConfig(bounds[position])
            );
        }
    }

    protected parseSprites(sprites: IObjectConfig[]): PIXI.Sprite[] {
        if (!sprites || !sprites.length) {
            return;
        }

        if (!this.sceneSprites) {
            this.sceneSprites = new Map<string, PIXI.Sprite>();
        }

        const startIndex = this.children.length ? this.children.length : 0;
        const list = [];
        for (const sprite in sprites) {
            const bounds = this.parseBoundConfig(sprites[sprite]);
            let obj: PIXI.Sprite;

            let key = sprites[sprite].id.split("~secondary")[0];

            if (this.sceneSprites.has(key)) {
                obj = this.sceneSprites.get(key);

                const newTexture = this.assets.getLayerTexture(
                    sprites[sprite].id,
                    sprites[sprite].parentLayer,
                    sprites[sprite].parentScene
                );
                if (sprites[sprite].id.indexOf("displace-") != -1) {
                    obj.texture = App.assets.getTexture(sprites[sprite].id)

                } else if (newTexture && newTexture !== obj.texture) {
                    obj.texture = newTexture;
                }
            } else {
                if (sprites[sprite].id.indexOf("displace-") != -1) {
                    obj = this.assets.getSprite(sprites[sprite].id)
                } else {

                    obj = this.assets.getLayerSprite(
                        sprites[sprite].id,
                        sprites[sprite].parentLayer,
                        sprites[sprite].parentScene
                    );

                }
                obj.name = sprites[sprite].id;
                this.sceneSprites.set(key, obj);
                this.addChildAt(obj, startIndex);
            }
            // const key = sprites[sprite].id.split("/")[1];
            this.layout.testSetWidth(obj, bounds.width);
            this.layout.setX(obj, bounds.x + bounds.width * obj.anchor.x);
            this.layout.setY(obj, bounds.y + bounds.height * obj.anchor.y);
            list.unshift(obj);
        }

        return list;
    }

    protected parseParticles(
        particles: IObjectConfig[],
        container: PIXI.Container
    ): PIXI.Container[] {
        if (!particles || !particles.length) {
            return;
        }

        if (!this.particles) {
            this.particles = new Map<string, Particles.Emitter>();
        }

        const list = [];

        for (const particle in particles) {
            const particleName = particles[particle].id.split("-")[1];
            const particleLib = particleName.split("~")[0];
            let bounds = this.parseBoundConfig(particles[particle]);
            let cont: PIXI.Container;
            let obj: Particles.Emitter;
            let emitting = true;
            if (this.particles.has(particleName)) {
                emitting = this.particles.get(particleName).emit;
                obj = this.particles.get(particleName);
                cont = obj.parent;
                // debugger;
                // obj.parent = cont;
                // cont.addChild(obj)
            } else {
                let data = App.assets.getParticleConfig(particleLib);
                if (data.spawnType === "point" || data.spawnType === "burst") {
                    cont = new PIXI.particles.ParticleContainer(5000, {
                        scale: true,
                        position: true,
                        rotation: false,
                        uvs: false,
                        tint: true,
                    });
                } else {
                    cont = new PIXI.Container();
                }

                container.addChild(cont);
                obj = App.assets.getParticle(particleLib, cont);
            }
            // obj = App.assets.getParticle(particleLib, cont);
            if (obj.spawnType === "circle" || obj.spawnType === "ring") {
                let circle: any = new PIXI.Circle(
                    Layout.scalePixels(bounds.x),
                    Layout.scalePixels(bounds.y),
                    Layout.scalePixels(bounds.width)
                );
                circle.minRadius = 1;
                obj.spawnCircle = circle;
            } else if (obj.spawnType === "point" || obj.spawnType === "burst") {
                obj.spawnPos = new PIXI.Point(
                    Layout.scalePixels(bounds.x + bounds.width / 2),
                    Layout.scalePixels(bounds.y + bounds.height / 2)
                );
            } else {
                obj.spawnRect = new PIXI.Rectangle(
                    Layout.scalePixels(bounds.x),
                    Layout.scalePixels(bounds.y),
                    Layout.scalePixels(bounds.width),
                    Layout.scalePixels(bounds.height)
                );
            }
            if (!(obj.spawnType === "point" || obj.spawnType === "burst")) {
                let mask = new PIXI.Graphics();
                mask.beginFill(0x000000, 0);
                mask.drawRect(
                    0,
                    0,
                    Layout.scalePixels(bounds.width),
                    Layout.scalePixels(bounds.height)
                );
                mask.x = Layout.scalePixels(bounds.x);
                mask.y = Layout.scalePixels(bounds.y);
                container.addChild(mask);
                cont.mask = mask;
            }

            if (!this.particles.has(particleName)) {

                this.particles.set(particleName, obj);
                ParticleLoop.add(particleName, obj);
            }
            list.unshift(cont);
            obj.emit = emitting;
        }

        return list;
    }

    protected parseAnims(anims: IObjectConfig[]): PIXI.extras.AnimatedSprite[] {
        if (!anims || !anims.length) {
            return;
        }

        if (!this.anims) {
            this.anims = new Map<string, PIXI.extras.AnimatedSprite>();
        }

        const list = [];
        for (const anim in anims) {
            const spriteName = anims[anim].id.split("-")[1];
            const numFrames = parseInt(anims[anim].id.split("-")[2]);
            const fps = parseInt(anims[anim].id.split("~")[1]);
            const counter = parseInt(anims[anim].id.split("~")[2]);
            let id = spriteName + (counter || "");
            let obj: PIXI.extras.AnimatedSprite;
            if (this.anims.has(id)) {
                obj = this.anims.get(id);
            } else {
                obj = new PIXI.extras.AnimatedSprite(
                    this.assets.generateFrames(spriteName + "_", 3, numFrames)
                );
                this.anims.set(id, obj);
                obj.animationSpeed = fps / 60;
                obj.play();
            }
            this.addChild(obj);
            const bounds = this.parseBoundConfig(anims[anim]);
            this.layout.testSetWidth(obj, bounds.width);
            this.layout.setY(obj, bounds.y);
            this.layout.setX(obj, bounds.x);
            list.unshift(obj);
        }

        return list;
    }

    protected parseTexts(texts: ITextConfig[], options?: ISceneOptions): PIXI.DisplayObject[] {
        if (!texts || !texts.length) {
            return;
        }
        if (!this.sceneTextFields) {
            this.sceneTextFields = new Map<
                string,
                PIXI.Text | BitmapTextField
            >();
        }
        const list = [];
        for (const text in texts) {
            const definition = texts[text];
            // const style = {...definition};
            const style = (({ width, height, x, y, id, content, ...o }) => o)(
                definition
            );
            // debugger;
            let bounds;
            let isCurrency = false;
            let hasOwnBounds = false;
            let isInput = false;

            let scrollable = false;
            let link = false;
            let isSelectedState = false;
            let isMultistyle = false;
            let id = definition.id;
            if (definition.id.indexOf("~multistyle") != -1) {
                isMultistyle = true;
                id = definition.id.split("~")[0];
            }
            if (definition.id.indexOf("~currency") != -1) {
                isCurrency = true;
                id = definition.id.split("~")[0];
            }

            if (definition.id.indexOf("~scrollable") != -1) {
                scrollable = true;
                id = definition.id.split("~")[0];
            }
            if (definition.id.indexOf("~link") != -1) {
                link = true;
                id = definition.id.split("~")[0];
            }
            if (id.indexOf("~selected") != -1) {
                isSelectedState = true;
                id = definition.id.split("~")[0] + "~selected";
            }
            if (id.indexOf("label-") != -1) {
                id = id.split("-")[1];
            }
            if (id.indexOf("input-") != -1) {
                id = id.split("-")[1];
                isInput = true;
            }

            const counter = parseInt(definition.id.split("~")[1]);
            if (counter && id.indexOf(counter.toString()) === -1) {
                id = id + "~" + counter;
            }
            if (this.bounds && this.bounds.has(id)) {
                bounds = this.bounds.get(id);
                hasOwnBounds = true;
            }

            if (isSelectedState) {
                bounds = this.bounds.get(id.split("~")[0]);
                hasOwnBounds = true;
            }

            bounds = this.parseBoundConfig(bounds);

            if (!bounds) {
                console.warn(
                    "PSD warning! Textfield with id : '" +
                    id +
                    "' doesn't have bounds"
                );
            }
            if (style.strokeThickness) {
                style.strokeThickness = Math.round(
                    this.layout.scalePixels(definition.strokeThickness)
                );
            }
            if (style.dropShadowDistance) {
                style.dropShadowDistance = Math.round(
                    this.layout.scalePixels(definition.dropShadowDistance)
                );
            }

            let multiplier = Layout.isLandscape()
                ? 1
                : Layout.portraitMultiplier;

            // if(this.orientation === ViewOrientation.Landscape) {
            style.fontSize = Math.max(
                Math.floor(
                    this.layout.scalePixels(definition.fontSize * multiplier)
                ),
                GameModel.gameConfig.minFontSize || 10
            );
            // } else {
            //     style.fontSize = Math.max( Math.floor(this.layout.scalePixels(definition.fontSize)), 10);
            // }

            if (isCurrency) {
                list.unshift(this.createCurrencyField(definition, bounds));
            } else if (isInput) {
                list.unshift(this.createInput(definition, bounds));
            } else {
                let obj: PIXI.Text | BitmapTextField | TextField;
                if (this.sceneTextFields.has(id)) {
                    obj = this.sceneTextFields.get(id);
                    if (obj instanceof TextField) {
                        //     obj.style = new PIXI.TextStyle(style);
                        obj.updateStyle(style);
                    }
                }

                if (definition.id.indexOf("label-") !== -1) {
                    let btnName = id;
                    if (isSelectedState) {
                        btnName = id.split("~")[0];
                    }
                    if (this.buttons.has(btnName)) {
                        if (!obj) {
                            obj = new TextField(
                                Locale.getString(definition.content),
                                style,
                                {
                                    multistyle: isMultistyle,
                                    bound: bounds,
                                    anchor: definition.anchor,
                                }
                            );
                            this.sceneTextFields.set(id, obj);
                        }

                        const textField = obj as TextField;

                        if (bounds !== definition && obj.anchor) {
                            textField.anchor.set(
                                definition.anchor.x,
                                this.layout.scalePixels(definition.anchor.y)
                            );
                        }

                        const btn = this.buttons.get(btnName);
                        if (isSelectedState && btn instanceof SwitchButton) {
                            btn.addSelectedLabelTF(
                                textField,
                                hasOwnBounds ? bounds : null
                            );
                        } else {
                            btn.addLabelTF(
                                textField,
                                hasOwnBounds ? bounds : null
                            );
                        }
                    }
                } else {
                    // let justCreated = false;
                    if (!obj) {
                        if (definition.fontVariant === "bmp") {
                            obj = new BitmapTextField(
                                Locale.getString(definition.content),
                                {
                                    font: {
                                        name: definition.fontFamily,
                                        size: style.fontSize,
                                    },
                                    align: style.align,
                                },
                                { bound: bounds, anchor: definition.anchor }
                            );
                        } else {
                            obj = new TextField(
                                Locale.getString(definition.content),
                                style,
                                {
                                    multistyle: isMultistyle,
                                    bound: bounds,
                                    anchor: definition.anchor,
                                }
                            );
                            obj.scrollable = scrollable;
                            if (link) {
                                if (!this.links) {
                                    this.links = new Map();
                                }
                                if (!this.links.has(id)) {
                                    let linkObj = new Link(obj);
                                    this.links.set(id, linkObj);
                                }
                            }

                        }
                        // justCreated = true;
                        this.sceneTextFields.set(id, obj);
                    }

                    if (definition.fontVariant === "bmp") {
                        const scaledWidth = this.layout.scalePixels(
                            bounds.width
                        );
                        const scaledHeight = this.layout.scalePixels(
                            bounds.height
                        );
                        (obj as BitmapTextField).font = {
                            name: definition.fontFamily,
                            size: style.fontSize,
                        };
                        (obj as BitmapTextField).anchor = new PIXI.Point(
                            definition.anchor.x,
                            0.5
                        );
                        (obj as BitmapTextField).bound = bounds;
                        (obj as BitmapTextField).applyBound();
                        // if (obj.width > scaledWidth) {
                        //     this.layout.testSetWidth(obj, bounds.width);
                        // }
                        // if (obj.height > scaledHeight) {
                        //     this.layout.testSetHeight(obj, bounds.height);
                        // }
                        // this.layout.setY(obj, bounds.y);
                        // this.layout.setX(obj, bounds.x + definition.anchor.x * bounds.width);
                    } else {
                        let textField = obj as TextField;
                        textField.bound = bounds;
                        // textField.anchor.set(definition.anchor.x, this.layout.scalePixels(definition.anchor.y));
                        textField.anchor = new PIXI.ObservablePoint(
                            null,
                            null,
                            definition.anchor.x,
                            this.layout.scalePixels(definition.anchor.y)
                        );
                        textField.applyBound(this.calculateTextMultiplier(), options?.textFieldApplyBound);
                    }
                    list.unshift(obj);
                    this.addChild(obj);
                }
            }
        }

        return list;
    }

    protected calculateTextMultiplier(): number {
        return 1;
    }

    protected createInput(
        definition: ITextConfig,
        bounds: IObjectConfig
    ): PIXI.DisplayObject {
        if (!this.inputTextFields) {
            this.inputTextFields = new Map<string, TextInput>();
        }
        let obj: TextInput;
        const id = definition.id.split("-")[1];
        if (this.inputTextFields.has(id)) {
            obj = this.inputTextFields.get(id);
            obj.styles = {
                input: {
                    height: this.layout.scalePixels(bounds.height) + "px",
                    width: this.layout.scalePixels(bounds.width) + "px",
                    textAlign: definition.align,
                    fontFamily: definition.fontFamily,
                    fontSize:
                        this.layout.scalePixels(definition.fontSize) + "px",
                    color: definition.fill[0],
                    fontWeight: "bold",
                },
            };
        } else {
            obj = new TextInput({
                input: {
                    height: this.layout.scalePixels(bounds.height) + "px",
                    width: this.layout.scalePixels(bounds.width) + "px",
                    textAlign: definition.align,
                    fontFamily: definition.fontFamily,
                    fontSize:
                        this.layout.scalePixels(definition.fontSize) + "px",
                    color: definition.fill[0],
                    fontWeight: "bold",
                },
            });
            this.addChild(obj as PIXI.Container);

            this.inputTextFields.set(id, obj);
        }

        obj.disabled = false;
        if (
            this.buttons.has(id) &&
            this.buttons.get(id) instanceof SwitchButton
        ) {
            const selector = this.buttons.get(id) as SwitchButton;
            obj.addSelector(selector);
        }

        obj.pivot.x = obj.width * definition.anchor.x;
        obj.pivot.y = obj.height * definition.anchor.x;

        this.layout.setY(obj as PIXI.Container, bounds.y + 0.5 * bounds.height);
        this.layout.setX(
            obj as PIXI.Container,
            bounds.x + definition.anchor.x * bounds.width
        );

        return obj as PIXI.Container;
    }

    protected createCurrencyField(
        definition: ITextConfig,
        bounds: IObjectConfig
    ): PIXI.DisplayObject {
        if (!this.sceneMoneyFields) {
            this.sceneMoneyFields = new Map<
                string,
                AbstractMoneyField | AbstractBitmapMoneyField
            >();
        }

        let id = definition.id.split("~")[0];
        const counter = parseInt(definition.id.split("~")[1]);
        if (counter && id.indexOf(counter.toString()) === -1) {
            id = id + "~" + counter;
        }
        const config = GameModel.defaultCurrencyFormat;
        // bounds.width = Layout.scalePixels(bounds.width);
        // bounds.height = Layout.scalePixels(bounds.height);
        let obj: AbstractMoneyField | AbstractBitmapMoneyField;
        if (this.sceneMoneyFields.has(id)) {
            obj = this.sceneMoneyFields.get(id);
            obj.bounds = bounds;
            if (obj instanceof AbstractBitmapMoneyField) {
                let font = new BitmapMoneyFieldConfig({
                    ...config,
                    ...{
                        bounds,
                        fontFamily: definition.fontFamily,
                        moneyFontSize: definition.fontSize,
                        align: definition.align,
                    },
                });
                obj.style = font;
            }
            if (obj instanceof AbstractMoneyField) {
                obj.style = definition;
            }
        } else {
            if (definition.fontVariant == "bmp") {
                obj = InstanceFactory.getClass("BitmapMoneyField",
                    new BitmapMoneyFieldConfig({
                        ...config,
                        ...{
                            bounds,
                            fontFamily: definition.fontFamily,
                            moneyFontSize: definition.fontSize,
                            align: definition.align,
                        },
                    })
                );
            } else {
                if (definition.id.indexOf("label-") !== -1 && !bounds) {
                    let n = definition.id.split("-")[1];
                    n = n.split("~")[0] + "~" + n.split("~")[1];
                    let btn: Button;
                    if (this.buttons.has(n)) {
                        btn = this.buttons.get(n);
                        let b = btn.getBounds();
                        bounds = {
                            id: definition.id,
                            x: b.x,
                            y: b.y,
                            width: b.width,
                            height: b.height,
                        };
                    }
                    obj = InstanceFactory.getClass("MoneyField",
                        new MoneyFieldConfig({
                            ...config,
                            ...{
                                bounds,
                                fontFamily: definition.fontFamily,
                                moneyFontSize: definition.fontSize,
                                align: definition.align,
                            },
                        })
                    ) as AbstractMoneyField;
                    obj.style = definition;
                    return;
                }
                obj = InstanceFactory.getClass("MoneyField",
                    new MoneyFieldConfig({
                        ...config,
                        ...{
                            bounds,
                            fontFamily: definition.fontFamily,
                            moneyFontSize: definition.fontSize,
                            align: definition.align,
                        },
                    })
                ) as AbstractMoneyField;
                obj.style = definition;
            }
            obj.value = parseFloat(definition.content);
        }
        this.addChild(obj);
        // Layout.testSetWidth(obj, bounds.width);
        // this.layout.setY(obj, bounds.y);
        // this.layout.setX(obj, bounds.x);
        obj.rescale(bounds);
        this.sceneMoneyFields.set(id, obj);
        return obj;
    }

    protected changeOrientation(newOrientation: ViewOrientation) {
        if (this.orientation === newOrientation) {
            return;
        }
        this.orientation = newOrientation;
        this.parseConfig();
    }

    public get currentSceneConfig(): IViewConfigSchema {
        if (
            this.orientation === ViewOrientation.Portrait &&
            this.sceneConfigPortrait
        ) {
            return this.sceneConfigPortrait;
        }

        return this.sceneConfig;
    }

    protected parseBoundConfig(bounds: IObjectConfig): IObjectConfig {
        if (!bounds) {
            return;
        }
        return {
            id: bounds.id,
            x: bounds.x,
            y: bounds.y,
            width: bounds.width,
            height: bounds.height,
        };
    }

    destroy(options?: any) {
        if (this.spineAnimations) {
            this.spineAnimations.forEach(spine => {
                spine.destroy()
            })
            this.spineAnimations = null
        }
        if (this.particles) {
            this.particles.forEach((val, key) => {
                ParticleLoop.remove(key);
            });
        }
        if (this.subViews) {
            this.subViews.forEach((view) => {
                if (view instanceof Array) {
                    view.forEach((child) => {
                        child.destroy();
                    });
                } else {
                    view.destroy();
                }
            });
        }
        super.destroy(options);
    }
}
